import { useMutation } from '@tanstack/react-query';

import { queryClient } from '~app/query-client';

import { baseAPI } from '~shared/api/base-api';
import { KEYS } from '~shared/api/query-keys';

import { type UpdateSubscriptionInvoicesResponse } from '../modal/payment';

interface LineItem {
  productName: string;
  amount: number;
  type: string;
}

export interface UpdateSubscriptionInvoiceLineItemsParams {
  studentId?: string;
  invoiceId: string;
  lineItems: Array<LineItem>;
}

export const updateSubscriptionInvoiceLineItems = async ({
  invoiceId,
  lineItems,
}: UpdateSubscriptionInvoiceLineItemsParams) => {
  const response = await baseAPI.put<UpdateSubscriptionInvoicesResponse>(
    `/subscription-invoices/update-line-items/${invoiceId}`,
    { lineItems },
  );
  return response.data;
};

export const useUpdateSubscriptionInvoiceLineItems = () => {
  return useMutation({
    mutationFn: (params: UpdateSubscriptionInvoiceLineItemsParams) =>
      updateSubscriptionInvoiceLineItems(params),
    onSuccess: (_, y) => {
      const studentId = y.studentId;
      void queryClient.invalidateQueries({
        queryKey: studentId
          ? [KEYS.UPCOMING_SUBSCRIPTION_INVOICES, studentId]
          : [KEYS.INVOICE_LINE_ITEMS, y.invoiceId],
      });
      // Invoice edits affect outstanding/aging → refresh Dashboard V2 AR
      void queryClient.invalidateQueries({ queryKey: [KEYS.ACCOUNTS_RECEIVABLE] });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.ACCOUNTS_RECEIVABLE_DUE_DATE_GROUPING],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.ACCOUNTS_RECEIVABLE_STUDENT_GROUPING],
      });
      // Line item edits change the invoice total shown in history views
      void queryClient.invalidateQueries({
        queryKey: [KEYS.STUDENT_PAYMENT_HISTORY],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.PARENT_PAYMENTS_HISTORY],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.OUTSTANDING_BALANCE],
      });
    },
  });
};
